
import { useState, useEffect, useCallback } from 'react';
import { isSupabaseAvailable } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';

export type ConnectionStatus = 'connected' | 'disconnected' | 'connecting' | 'unknown';
export type ConnectionStatusStrict = 'connected' | 'disconnected' | 'connecting';

/**
 * Hook to manage and monitor connection state with improved reliability
 */
export const useConnectionState = (
  autoCheck: boolean = true,
  checkInterval: number = 60000 // 1 minute by default
) => {
  const [status, setStatus] = useState<ConnectionStatus>('unknown');
  const [lastChecked, setLastChecked] = useState<number>(0);
  const [checkCount, setCheckCount] = useState<number>(0);
  const { toast } = useToast();
  
  // Check connection with debounce
  const checkConnection = useCallback(async (showToasts: boolean = false) => {
    // Don't check too frequently
    const now = Date.now();
    if (now - lastChecked < 5000 && checkCount > 0) {
      console.log('Skipping connection check - too recent');
      return status === 'connected';
    }
    
    // Update check count
    setCheckCount(prev => prev + 1);
    setLastChecked(now);
    
    const prevStatus = status;
    setStatus('connecting');
    
    try {
      const isConnected = await isSupabaseAvailable();
      const newStatus = isConnected ? 'connected' : 'disconnected';
      setStatus(newStatus);
      
      // Only show toast for disconnect events, not reconnect events
      if (showToasts && prevStatus !== 'unknown' && prevStatus !== newStatus) {
        if (newStatus === 'disconnected' && prevStatus === 'connected') {
          toast({
            title: "Mất kết nối",
            description: "Kết nối đến máy chủ bị gián đoạn. Đang chuyển sang chế độ ngoại tuyến.",
            variant: "destructive"
          });
        }
      }
      
      return isConnected;
    } catch (error) {
      console.error('Error checking connection:', error);
      setStatus('disconnected');
      
      if (showToasts && prevStatus !== 'disconnected') {
        toast({
          title: "Lỗi kết nối",
          description: "Không thể kết nối đến máy chủ.",
          variant: "destructive"
        });
      }
      
      return false;
    }
  }, [status, lastChecked, checkCount, toast]);
  
  // Set up automatic checking
  useEffect(() => {
    if (!autoCheck) return;
    
    // Initial check
    checkConnection();
    
    // Set up periodic checks
    const interval = setInterval(() => {
      if (status === 'disconnected') {
        checkConnection(true); // Show toasts on reconnection
      } else {
        checkConnection(false); // Silent check
      }
    }, checkInterval);
    
    // Check when tab becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const now = Date.now();
        const lastCheckedAge = now - lastChecked;
        
        // Only check if it's been a while since the last check
        if (lastCheckedAge > 60000) { // 1 minute
          checkConnection(true);
        }
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [autoCheck, checkInterval, status, checkConnection, lastChecked]);
  
  // Get a filtered status without 'unknown' for components that expect stricter types
  const getFilteredStatus = (): ConnectionStatusStrict => {
    return status === 'unknown' ? 'disconnected' : status;
  };
  
  return {
    status,
    filteredStatus: getFilteredStatus(),
    isConnected: status === 'connected',
    isDisconnected: status === 'disconnected',
    isConnecting: status === 'connecting',
    isUnknown: status === 'unknown',
    checkConnection,
    lastChecked
  };
};
